class CountryCurrencySelector extends window.BAO.CustomElement() {
  testMode = false
  userLocationName = 'BAO-user-location-refy'
  hasMadeSelectionName = 'BAO-has-made-selection-refy'
  euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE']
  geoSelectorKey = 'geo-selector'

  static get requiredElements () {
    return ['option']
  }

  connectedCallback () {
    super.connectedCallback()

    // Initialise the geolocation modal.
    if (this.isGeo && this.els['geo-current']?.exists) {
      this.shadow = this.els['shadow-store'].element.attachShadow({ mode: 'open' })
      this.shadowContent = this.querySelector('[data-shadow]').content

      this.shouldOpenGeolocationModal()
    }
  }

  setupListeners () {
    super.setupListeners()

    // Init these here so they are available for the listeners.
    // setupListeners is run before connectedCallback.
    const { currencyCode, store, isGeolocationModal } = this.dataset
    this.currency = this.targetCurrency = currencyCode
    this.store = this.targetStore = store
    this.isGeo = isGeolocationModal === 'true'

    this.handleOptionClick = this.handleOptionClick.bind(this)
    this.handleGeoButtonClick = this.handleGeoButtonClick.bind(this)
    this.handleGeoSelectChange = this.handleGeoSelectChange.bind(this)
    this.close = this.close.bind(this)
    this.switchCurrentCountry = this.switchCurrentCountry.bind(this)

    this.els.option.elements.forEach(option => {
      this.listeners.add(option, 'click', this.handleOptionClick)
    })

    if (this.externalEls['geo-close']?.exists) {
      this.listeners.add(this.externalEls['geo-close'].element, 'click', this.close)
    }

    if (this.els['geo-select']?.exists) {
      this.listeners.add(this.els['geo-select'].element, 'change', this.handleGeoSelectChange)
    }

    if (this.els['geo-button']?.exists) {
      this.listeners.add(this.els['geo-button'].element, 'click', this.handleGeoButtonClick)
    }
  }

  handleOptionClick (event) {
    const target = event.target.closest('[data-country-currency-selector-el="option"]')
    if (!target) return

    this.targetStore = target.dataset.store
    this.targetCurrency = target.dataset.currencyCode

    this.switchStore()
  }

  handleGeoSelectChange (e) {
    const newCountryValue = e.target.value

    // Change label to new value.
    const countryHtml = this.shadowContent.querySelector(`[data-country-code="${newCountryValue}"]`)
    this.switchCurrentCountry(countryHtml, newCountryValue)
  }

  handleGeoButtonClick () {
    const selectedOption = this.els['geo-select'].element.options[this.els['geo-select'].element.selectedIndex]

    this.targetStore = selectedOption.dataset.store
    this.targetCurrency = selectedOption.dataset.currencyCode

    this.switchStore()
  }

  /**
   * Switches the store and/or currency based on user selection.
   * Updates the URL search parameters accordingly.
   */
  switchStore () {
    const searchParams = new URLSearchParams(window.location.search)

    // If users clicked on the same store AND the same currency, we need to close the dropdown.
    if (this.targetStore === this.store && this.targetCurrency === this.currency) {
      this.close()
      return
    }

    // If users clicked on the same store AND different currency, we need to switch the currency.
    if (this.targetStore === this.store) {
      this.updateSearchParams(searchParams, { currency: this.targetCurrency })
      return
    }

    this.setHasMadeSelection()

    // If users clicked on a different store, we need to redirect to the new store with the currency.
    this.goToStore()
  }

  goToStore () {
    const searchParams = new URLSearchParams(window.location.search)
    const currentPath = window.location.pathname;

    if (this.targetStore.substring(0, 8) !== 'https://') {
      this.targetStore = `https://${this.targetStore}`
    }

    const newUrlWithPath = new URL(`${this.targetStore}${currentPath}?${searchParams.toString()}`).toString()

    return window.location.href = newUrlWithPath
  }

  /**
   * Updates the URL search parameters with the given parameters.
   *
   * @param {URLSearchParams} searchParams - The current URL search parameters.
   * @param {Object} params - The parameters to update in the searchParams.
   */
  updateSearchParams (searchParams, params) {
    Object.keys(params).forEach(key => searchParams.set(key, params[key]))

    window.location.search = searchParams.toString()
  }

  close () {
    window.drawers.activeDrawerKey = ''

    if (this.isGeo) {
      window.localStorage.setItem(this.hasMadeSelectionName, 'true')
    }
  }

  /**
   * Removes the 'currency' search parameter from the URL once the page is loaded.
   */
  removeCurrencyParamFromUrl () {
    const searchParams = new URLSearchParams(window.location.search)

    if (searchParams.has('currency')) {
      searchParams.delete('currency')

      const newUrl = `${window.location.pathname}?${searchParams.toString()}`

      window.history.replaceState({}, '', newUrl)
    }
  }

  async toggleModal () {
    await this.constructModal()

    drawers.activeDrawerKey = drawers.activeDrawerKey === this.geoSelectorKey ? '' : this.geoSelectorKey
  }

  async constructModal () {
    const isInEu = this.euCountries.includes(this.userLocation)
    if (isInEu) {
      this.userLocation = 'EU'
    }

    // Find the corresponding country in the shadow dom
    const country = this.shadowContent.querySelector(`[data-country-code="${this.userLocation}"]`)
    this.switchCurrentCountry(country, this.userLocation)
  }

  switchCurrentCountry (country, locationCode) {
    this.els['geo-current'].element.innerHTML = country.innerHTML
    this.els['geo-select'].element.value = locationCode
  }

  async shouldOpenGeolocationModal () {
    try {
      if (this.testMode === true) {
        await this.locateCustomer()
        await this.toggleModal()
        return
      }

      const isUKStore = this.store === 'refybeauty.myshopify.com'
      if (!isUKStore || Shopify.designMode || this.hasMadeSelection) return

      const [userLocation, isInEU] = await this.locateCustomer()
      if (typeof userLocation !== 'string' || userLocation.length === 0) return

      // We only want to show the modal to customers in this set of places.
      const customerIsInOneOfThesePlaces = isInEU || ['US', 'CA'].includes(userLocation)
      if (customerIsInOneOfThesePlaces) {
        await this.toggleModal()
      }
    } catch (error) {
      console.error('Error in shouldOpenGeolocationModal:', error)
    }
  }

  async locateCustomer () {
    try {
      let userLocation = null

      // Read stored user location.
      // If not found, read location from API.
      if (this.isLocationStored) {
        userLocation = window.localStorage.getItem(this.userLocationName)
      } else {
        userLocation = await this.getCustomerLocationData()

        window.localStorage.setItem(this.userLocationName, userLocation.detected_values?.country?.handle)
      }

      // Check if user is in EU.
      const country = userLocation.detected_values?.country?.handle || userLocation
      const isInEU = this.euCountries.includes(country)

      this.userLocation = country

      return [country, isInEU]
    } catch (error) {
      console.error('Error locating customer:', error)
      return []
    }
  }

  async getCustomerLocationData () {
    return fetch('/browsing_context_suggestions.json').then(response => response.json())
  }

  setHasMadeSelection () {
    window.localStorage.setItem(this.hasMadeSelectionName, 'true')
  }

  get hasMadeSelection () {
    return window.localStorage.getItem(this.hasMadeSelectionName) === 'true'
  }
}

if (!customElements.get('country-currency-selector')) {
  customElements.define('country-currency-selector', CountryCurrencySelector)
}
